import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Mail, Phone, ArrowRight } from "lucide-react";
import QstLayout from "./QstLayout";
import { qstContact } from "./api";

export default function QstContactPage() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", role: "",
    intent: params.get("intent") || "question",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { document.title = "Contact — Quality Safety Times"; }, []);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await qstContact(form);
      setSent(true);
    } catch (e) {
      setErr("Could not submit. Please try again or email us directly.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <QstLayout>
      <section className="bg-slate-50 py-14 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">Contact</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-black text-[#0B2545]">Let's build safer, better projects together.</h1>
          <p className="mt-3 text-lg text-slate-700 max-w-2xl">
            Tell us about your crews and projects — we'll follow up within one business day.
          </p>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10">
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          {sent ? (
            <div className="text-center py-10" data-testid="qst-contact-sent">
              <CheckCircle2 className="w-12 h-12 text-[#059669] mx-auto" />
              <h2 className="mt-4 text-2xl font-black text-[#0B2545]">Thanks — we got it.</h2>
              <p className="mt-2 text-slate-700">A QST team member will follow up within one business day.</p>
              <Link to="/qst/library?free_only=1"
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#F97316] hover:bg-[#EA580C] text-white px-6 py-3 font-bold">
                Explore free lessons while you wait <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="qst-contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Your name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="qst-contact-name" />
                <Field label="Email"      required type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="qst-contact-email" />
                <Field label="Phone"      value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="qst-contact-phone" />
                <Field label="Company"    value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="qst-contact-company" />
                <Field label="Role"       value={form.role} onChange={(v) => setForm({ ...form, role: v })} testid="qst-contact-role" />
                <label className="text-sm">
                  <div className="text-slate-700 font-semibold">Reason for contact</div>
                  <select value={form.intent} onChange={(e) => setForm({ ...form, intent: e.target.value })}
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
                    data-testid="qst-contact-intent">
                    <option value="question">General question</option>
                    <option value="trial">Start a trial</option>
                    <option value="demo">Book a demo</option>
                    <option value="enterprise">Enterprise / bulk pricing</option>
                    <option value="waitlist">Trade waitlist</option>
                  </select>
                </label>
              </div>
              <label className="block text-sm">
                <div className="text-slate-700 font-semibold">Tell us about your project</div>
                <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#0B2545] focus:ring-2 focus:ring-[#0B2545]/10 outline-none"
                  data-testid="qst-contact-message" />
              </label>
              {err && <div className="text-sm text-red-700">{err}</div>}
              <button type="submit" disabled={busy}
                className="inline-flex items-center gap-2 rounded-md bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-50 text-white px-6 py-3 font-bold shadow-sm"
                data-testid="qst-contact-submit">
                {busy ? "Sending…" : <>Send message <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          )}
        </div>

        <div className="space-y-5">
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-600">Direct</div>
            <div className="mt-3 flex items-center gap-2 text-slate-800">
              <Mail className="w-4 h-4 text-[#F97316]" />
              <a href="mailto:michaelclarke@qualitysafetytimes.com" className="hover:underline">michaelclarke@qualitysafetytimes.com</a>
            </div>
            <div className="mt-2 flex items-center gap-2 text-slate-800">
              <Phone className="w-4 h-4 text-[#F97316]" />
              <a href="tel:3606019859" className="hover:underline">360-601-9859</a>
            </div>
            <div className="mt-4 text-sm text-slate-600">
              <strong>Michael Clarke</strong><br />Business Development
            </div>
          </div>
          <div className="rounded-xl border-2 border-[#0B2545] bg-[#0B2545]/[0.04] p-6">
            <div className="text-sm font-bold text-[#0B2545]">Bulk / multi-org pricing</div>
            <div className="mt-2 text-xs text-slate-700 leading-relaxed">
              Trade Associations · Insurers · Insurance Brokers · OCIP/CCIP Administrators · General Contractors · Municipalities
              with contractor mentorship programs.
            </div>
          </div>
        </div>
      </section>
    </QstLayout>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="text-sm">
      <div className="text-slate-700 font-semibold">{label}{required && <span className="text-red-600">*</span>}</div>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#0B2545] focus:ring-2 focus:ring-[#0B2545]/10 outline-none"
        data-testid={testid} />
    </label>
  );
}
